import React from "react";
import Image from "next/image";
import { toast } from "sonner";

import { PaymentCalculator } from "@/backend/domains/subscription/application/payment.calculator";
import { paymentPlanPeriods } from "@/backend/domains/subscription/data/paymentPlans";
import { ISubscription } from "@/backend/domains/subscription/domain/Subscription";
import { useModal } from "@/client/shared/hooks/useModal";

import { execAction } from "@/lib/executeServerAction";
import { cn } from "@/lib/utils";
import { useHandler } from "@/hooks/useHandler";
import { useServerAction } from "@/hooks/useServerAction";
import { subscriptionStore, useMutateSubscription } from "@/stores/subscription.store";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DialogContainer } from "@/components/ui/dialog";
import { InfoButton } from "@/components/ui/info-button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Typography } from "@/components/ui/typography";
import { clientApi } from "@/actions";
import delay from "@/utils/delay";
import { doIt } from "@/utils/doIt";
import formatter from "@/utils/formatter";
import { plural } from "@/utils/plural";

import { usePlanActivationStore } from "../..";
import { PaymentCardsTabProps } from "./PaymentCardsTab";

//

interface IndexTabProps {}

export const IndexTab: React.FC<IndexTabProps> = () => {
  const planType = usePlanActivationStore((s) => s.plan.type);
  const tabsModel = usePlanActivationStore((s) => s.tabsModel);
  const paymentData = usePlanActivationStore((s) => s.paymentData);
  const periodItem = usePlanActivationStore((s) => s.periodItem);
  const mutateSubscription = useMutateSubscription();
  const { close } = useModal();
  const mutateSubscriptionData = subscriptionStore.useContext((s) => s.mutateSubscriptionData);

  const nowPaymentData = PaymentCalculator.getNowPayment(paymentData);

  const { data: paymentCardsData, mutate: mutateCards } = useServerAction(
    { key: "/payment-cards", organizationId: true },
    clientApi.paymentCards.findCardsWithPriority,
  );

  const payHandler = useHandler(
    async () => {
      if (!paymentCardsData) throw new Error("Данные платежных карт еще не загрузились.");

      const { cards } = paymentCardsData;
      const hasCard = cards.length > 0;

      const action = clientApi.invoice.createSubscriptionInvoice;
      const invoice = await execAction(() => action(periodItem.id, planType));

      const { amount, firstPaymentNow } = nowPaymentData;

      async function handleSuccessPayment({ transactionId }: { transactionId?: number }) {
        const { Model } = transactionId
          ? await execAction(() => clientApi.paymentSystem.getTransaction(transactionId))
          : {};

        const createMutateData = async (counter: number = 0): Promise<ISubscription> => {
          const subscription = await execAction(() => clientApi.subscription.getSubscription());
          if (counter >= 30) return subscription;
          if (!Model) return subscription;
          if (subscription.paymentSubscriptionId === Model.SubscriptionId) return subscription;
          await delay(300);
          return await createMutateData(counter + 1);
        };

        close();

        const promise = async () => {
          await mutateSubscription(() => createMutateData());
          await mutateSubscriptionData((d) =>
            d ? { invoice, paymentPlan: d.paymentPlan } : undefined,
          );
          await mutateCards(undefined);
        };

        toast.promise(promise, {
          loading: "Активация тарифа...",
          success: "Тариф активирован",
          error: (error) => `Ошибка: ${JSON.stringify(error, null, 2)}`,
        });
      }

      const props: PaymentCardsTabProps = {
        invoice,
        onSuccess: handleSuccessPayment,
        buttonText: firstPaymentNow ? undefined : "Подтвердить карту",
        title: firstPaymentNow ? undefined : "Подтверждение карты",
        caption: firstPaymentNow ? undefined : (
          <Typography muted>
            Для активации тарифа временно спишем {formatter.currency(amount)} — сумма сразу вернётся
            и после активируется тариф.
          </Typography>
        ),
      };

      if (!hasCard) return tabsModel.setTab("checkoutTab", props);
      return tabsModel.setTab("paymentCardsTab", props);
    },
    { disableSuccessMessage: true },
  );

  const nextPlanHandler = useHandler(async () => {
    const action = clientApi.invoice.createSubscriptionInvoice;
    const invoice = await execAction(() => action(periodItem.id, planType));

    const activateNextPlan = clientApi.subscription.setNextPlan;
    const subscription = await execAction(() => activateNextPlan(planType, invoice.id));

    await mutateSubscription(subscription);
    return close();
  });

  const enableAutoRenewalHandler = useHandler(async () => {
    const invoice = await execAction(() =>
      clientApi.invoice.createSubscriptionInvoice(periodItem.id, planType),
    );

    const action = clientApi.subscription.enableAutoRenewalSubscription;
    const subscription = await execAction(() => action(invoice.id));

    await mutateSubscription(subscription);
    return close();
  });

  return (
    <div className="flex flex-col overflow-auto">
      <DialogContainer className="grid gap-3">
        <div className="flex items-center gap-3">
          <Image alt="" src={"/images/diamond.gif"} width={50} height={50} />
          <div>
            <Typography bold h4 text={planType} className="leading-[1.25]" />
            <Typography muted sm text="Подписка" />
          </div>
        </div>
        <PeriodSelecter />
      </DialogContainer>
      <Separator />
      <DialogContainer className="flex flex-col gap-3" last>
        <PaymentInfo />
        <Separator />
        <Typography sm muted>
          Можно отменить или перейти на другой тариф без переплат в любой момент. Подписка
          продляется автоматически 1-го числа каждого месяца. Первый платеж учитывает только
          оставшиеся дни месяца.
        </Typography>
        {doIt(() => {
          if (paymentData.isEnablingAutoRenewal) {
            return (
              <Button
                onClick={enableAutoRenewalHandler.handler}
                loading={enableAutoRenewalHandler.isLoading}
              >
                Активировать
              </Button>
            );
          }

          if (paymentData.isUpgrade === false) {
            return (
              <Button onClick={nextPlanHandler.handler} loading={nextPlanHandler.isLoading}>
                Сменить тариф к {formatter.date(paymentData.firstPayment.planActivatedAt)}
              </Button>
            );
          }

          return (
            <Button
              onClick={payHandler.handler}
              loading={payHandler.isLoading || !paymentCardsData}
            >
              {nowPaymentData.firstPaymentNow
                ? `Перейти к оплате | ${formatter.currency(paymentData.firstPayment.amount)}`
                : "Продолжить"}
            </Button>
          );
        })}
      </DialogContainer>
    </div>
  );
};

//

const PeriodSelecter: React.FC<{ className?: string }> = ({ className }) => {
  const setPeriod = usePlanActivationStore((s) => s.setPeriodById);
  const period = usePlanActivationStore((s) => s.periodItem);

  return (
    <Select value={period.id} onValueChange={(v) => setPeriod(v)}>
      <SelectTrigger className={cn("", className)} autoFocus={false}>
        <SelectValue placeholder="Период" />
      </SelectTrigger>
      <SelectContent>
        {paymentPlanPeriods.map(({ period, discount, interval, id }) => (
          <SelectItem key={id} value={id}>
            <div className="flex items-center gap-2">
              <span>{PaymentCalculator.getPeriodString(period, interval)}</span>
              {discount > 0 && (
                <Badge variant={"red"} className="px-1 py-0">
                  {formatter.percentage(-discount)}
                </Badge>
              )}
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

//

interface PaymentInfoProps {}

const PaymentInfo: React.FC<PaymentInfoProps> = () => {
  const paymentData = usePlanActivationStore((s) => s.paymentData);
  const tabsModel = usePlanActivationStore((s) => s.tabsModel);

  const firstPayment = paymentData.firstPayment.amount;
  const nextPayment = paymentData.nextPayment.amount;
  const nextPaymentDate = paymentData.nextPayment.date;
  const { activateNow, planActivatedAt } = paymentData.firstPayment;

  const { firstPeriodString, nextPeriodsString, promoText } =
    PaymentCalculator.getText(paymentData);

  return (
    <div className="flex flex-col gap-3">
      {activateNow === false &&
        paymentData.isUpgrade === false &&
        !paymentData.isEnablingAutoRenewal && (
          <div className="bg-muted text-muted-foreground -mx-2 flex items-center gap-1 self-start rounded-lg px-2 py-1 font-semibold">
            <Typography sm>Тариф будет активирован {formatter.date(planActivatedAt)}</Typography>
            <InfoButton
              onClick={() =>
                tabsModel.setTab("infoTab", {
                  children: (tabsModel) => (
                    <div className="grid gap-4 text-center">
                      <div className="grid gap-3">
                        <Image
                          alt="sad-brain"
                          src="/images/scroll-down.gif"
                          className="m-auto"
                          width={100}
                          height={100}
                        />
                        <Typography h4>Понижение тарифа</Typography>
                      </div>
                      <div className="grid gap-3">
                        <Typography size="p" muted>
                          Вы понижаете тариф. Он будет активирован, когда завершится текущий.
                        </Typography>
                        <Typography size="p" muted>
                          При повышении тарифа, вы доплачиваете разницу и тариф активируется сразу.
                        </Typography>
                      </div>
                      {tabsModel.canBack() && (
                        <Button
                          variant={"outline"}
                          icon={"ArrowLeft"}
                          onClick={() => tabsModel.back()}
                        >
                          Назад
                        </Button>
                      )}
                    </div>
                  ),
                })
              }
            />
          </div>
        )}

      {promoText && (
        <div className="-mx-2 mb-0.5 self-start rounded-lg bg-emerald-100 px-2 py-1 font-bold text-emerald-600">
          <Typography>{promoText}</Typography>
        </div>
      )}

      {paymentData.trialDays !== 0 && (
        <InfoRow
          title={`${plural(paymentData.trialDays, "день", "дня", "дней")} бесплатно`}
          caption="Начиная сегодня"
        />
      )}
      {firstPayment > 0 && (
        <InfoRow
          title={`${formatter.currency(firstPayment)} первый платеж`}
          caption={doIt(() => {
            const dateString = paymentData.firstPayment.payNow
              ? "Сейчас"
              : formatter.date(paymentData.firstPayment.date);
            return `${dateString} за ${firstPeriodString}`;
          })}
        />
      )}
      <InfoRow
        title={`${formatter.currency(nextPayment)} ${nextPeriodsString}`}
        caption={`Начиная с ${formatter.date(nextPaymentDate)}`}
      />
      <div className="overflow-auto" hidden={true}>
        <pre className="text-xs">
          {JSON.stringify(
            paymentData,
            function (key) {
              const value = this[key];
              if (value instanceof Date) return formatter.dateTime(value);
              return value;
            },
            2,
          )}
        </pre>
      </div>
    </div>
  );
};

//

interface InfoRowProps {
  className?: string;
  title?: React.ReactNode;
  caption?: React.ReactNode;
}

const InfoRow: React.FC<InfoRowProps> = ({ className, title, caption }) => {
  return (
    <div className={cn("flex flex-col", className)}>
      {title && (
        <Typography bold size="p" className="leading-none">
          {title}
        </Typography>
      )}
      {caption && <Typography muted>{caption}</Typography>}
    </div>
  );
};
